import React, { useEffect, useMemo, useState } from "react";
import { api, fmt } from "../../../services/erpApi";
import { StatCard, PageHeader, LoadingScreen, EmptyState } from "../../../components/admin/EssUi";
import { Search, Building2, MapPin, Package, FolderArchive } from "lucide-react";

const AdminPanelWarehousesPage = () => {
  const [warehouses, setWarehouses] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [usageFilter, setUsageFilter] = useState("all");
  const [whFilter, setWhFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [activeView, setActiveView] = useState("warehouses");

  useEffect(() => {
    (async () => {
      await Promise.all([
        api.inventory.warehouses().catch(() => []),
        api.inventory.locations({}).catch(() => []),
      ]).then(([w, l]) => {
        setWarehouses(w || []);
        setLocations(l || []);
      });
      setLoaded(true);
    })();
  }, []);

  const stats = useMemo(() => {
    const totalQty = warehouses.reduce((s, w) => s + (Number(w.total_qty) || 0), 0);
    const totalLoc = locations.length;
    return { whCount: warehouses.length, totalQty, totalLoc };
  }, [warehouses, locations]);

  const filteredLocations = useMemo(() => {
    return locations.filter((l) => {
      if (usageFilter !== "all" && l.usage !== usageFilter) return false;
      if (whFilter !== "all" && String(l.warehouse_id) !== whFilter) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const hay = `${l.name || ""} ${l.complete_name || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [locations, usageFilter, whFilter, search]);

  const usageLabel = (u) => {
    const map = { internal: "Internal", supplier: "Supplier", customer: "Customer", inventory: "Inventory", production: "Production", transshipment: "Transit" };
    return map[u] || u || "—";
  };
  const usageColor = (u) => {
    if (u === "internal") return { text: "#225f2b", bg: "#f0f7f0" };
    if (u === "supplier") return { text: "#b45309", bg: "#fffbeb" };
    if (u === "customer") return { text: "#225f2b", bg: "#f0f7f0" };
    if (u === "inventory") return { text: "#be123c", bg: "#fff1f2" };
    return { text: "#475569", bg: "#f1f5f9" };
  };

  const inputStyle = {
    width: "100%", padding: "9px 12px", border: "1px solid #cbd5e1", borderRadius: "6px",
    fontSize: "13.5px", outline: "none", background: "#fff", boxSizing: "border-box",
  };

  if (!loaded) return <LoadingScreen />;

  return (
    <div className="max-w-7xl mx-auto animate-fade-in" style={{ padding: "0 16px" }}>
      <PageHeader title="Warehouses & Locations" subtitle="Inventory storage structure" />

      <div className="ess-sg ess-sg4" style={{ marginBottom: "24px" }}>
        <StatCard label="Warehouses" value={String(stats.whCount)} icon={Building2} color="indigo" trend="Active warehouses" />
        <StatCard label="Units Stored" value={fmt.qty(stats.totalQty)} icon={Package} color="blue" trend="Total across warehouses" />
        <StatCard label="Locations" value={String(stats.totalLoc)} icon={MapPin} color="green" trend="All locations" />
        <StatCard label="Internal Locations" value={String(locations.filter((l) => l.usage === "internal").length)} icon={FolderArchive} color="teal" trend="Stockable" />
      </div>

      <div style={{ display: "flex", gap: "24px", marginBottom: "20px", borderBottom: "1px solid #e2e7e2", overflowX: "auto" }}>
        {[
          { key: "warehouses", label: "Warehouses", count: warehouses.length },
          { key: "locations", label: "Locations", count: locations.length },
        ].map((t) => (
          <button key={t.key} onClick={() => setActiveView(t.key)} style={{
            padding: "9px 2px", border: "none", background: "transparent",
            borderBottom: `2px solid ${activeView === t.key ? "#225f2b" : "transparent"}`,
            marginBottom: -1, whiteSpace: "nowrap",
            color: activeView === t.key ? "#225f2b" : "#64748b", fontSize: "13px",
            fontWeight: activeView === t.key ? "600" : "500", cursor: "pointer",
            display: "flex", alignItems: "center", gap: "6px"
          }}>
            {t.label}
            <span style={{ background: activeView === t.key ? "#f0f7f0" : "#f1f5f9", color: activeView === t.key ? "#225f2b" : "#64748b", padding: "1px 8px", borderRadius: "10px", fontSize: "11px", fontWeight: "600" }}>{t.count}</span>
          </button>
        ))}
      </div>

      {activeView === "warehouses" ? (
        warehouses.length === 0 ? (
          <EmptyState icon="🏢" title="No warehouses" subtitle="Warehouses will appear once configured on the backend" />
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "16px" }}>
            {warehouses.map((w) => (
              <div key={w.id} style={{ background: "white", borderRadius: "8px", border: "1px solid #e2e7e2", padding: "20px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
                  <div style={{ width: 40, height: 40, borderRadius: "10px", background: "#f0f7f0", color: "#225f2b", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Building2 size={20} />
                  </div>
                  <div>
                    <h3 style={{ fontSize: "15px", fontWeight: "600", color: "#1e293b", margin: 0 }}>{w.name || "—"}</h3>
                    <p style={{ fontSize: "12px", color: "#94a3b8", margin: 0 }}>{w.code || w.complete_name || ""}</p>
                  </div>
                </div>
                <div style={{ display: "grid", gap: "12px" }} className="ess-rg-2">
                  <div style={{ background: "#f8fafc", borderRadius: "8px", padding: "10px", textAlign: "center" }}>
                    <div style={{ fontSize: "18px", fontWeight: "700", color: "#1e293b" }}>{fmt.qty(w.total_qty)}</div>
                    <div style={{ fontSize: "11px", color: "#64748b" }}>Units</div>
                  </div>
                  <div style={{ background: "#f8fafc", borderRadius: "8px", padding: "10px", textAlign: "center" }}>
                    <div style={{ fontSize: "18px", fontWeight: "700", color: "#1e293b" }}>{w.location_count ?? "—"}</div>
                    <div style={{ fontSize: "11px", color: "#64748b" }}>Locations</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        <>
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "24px", background: "white", padding: "16px", borderRadius: "8px", border: "1px solid #e2e7e2" }}>
            <div style={{ flex: 1, minWidth: "200px", position: "relative" }}>
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#94a3b8" }} />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search location..." style={{ ...inputStyle, padding: "10px 12px 10px 36px" }} />
            </div>
            <select value={usageFilter} onChange={(e) => setUsageFilter(e.target.value)} style={{ ...inputStyle, minWidth: "150px", cursor: "pointer" }}>
              <option value="all">All Usages</option>
              <option value="internal">Internal</option>
              <option value="supplier">Supplier</option>
              <option value="customer">Customer</option>
              <option value="inventory">Inventory</option>
              <option value="production">Production</option>
            </select>
            <select value={whFilter} onChange={(e) => setWhFilter(e.target.value)} style={{ ...inputStyle, minWidth: "180px", cursor: "pointer" }}>
              <option value="all">All Warehouses</option>
              {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </div>

          <div style={{ background: "white", borderRadius: "8px", border: "1px solid #e2e7e2", overflow: "hidden" }}>
            {filteredLocations.length === 0 ? (
              <EmptyState icon="📍" title="No locations" subtitle="Locations will appear once configured on the backend" />
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                  <thead>
                    <tr>
                      <th className="ess-th" style={{ minWidth: "200px" }}>Location</th>
                      <th className="ess-th" style={{ minWidth: "160px" }}>Warehouse</th>
                      <th className="ess-th" style={{ minWidth: "110px" }}>Usage</th>
                      <th className="ess-th ess-th-r" style={{ minWidth: "90px" }}>Barcode</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLocations.map((l) => (
                      <tr key={l.id} className="ess-tr">
                        <td className="ess-td" style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <MapPin size={14} style={{ color: "#94a3b8", flexShrink: 0 }} />
                          <span style={{ fontSize: "13px", fontWeight: "500", color: "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{l.name || "—"}</span>
                        </td>
                        <td className="ess-td" style={{ color: "#475569", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{l.warehouse_name || "—"}</td>
                        <td className="ess-td" style={{ whiteSpace: "nowrap" }}>
                          <span style={{ background: usageColor(l.usage).bg, color: usageColor(l.usage).text, padding: "3px 10px", borderRadius: "6px", fontSize: "12px", fontWeight: "600", whiteSpace: "nowrap" }}>{usageLabel(l.usage)}</span>
                        </td>
                        <td className="ess-td" style={{ fontSize: "12px", color: "#64748b", textAlign: "right", whiteSpace: "nowrap" }}>{l.barcode || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AdminPanelWarehousesPage;
